import { t } from 'i18next';
import _ from 'lodash';
import { ReactNode, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Avatar } from '../../../../../../shared/frontend/components/avatar/Avatar';
import { Form } from '../../../../../../shared/frontend/components/form/Form';
import { FormControl } from '../../../../../../shared/frontend/components/form/FormControl';
import { FormControlHelpText } from '../../../../../../shared/frontend/components/form/FormControlHelpText';
import { FormControlLabel } from '../../../../../../shared/frontend/components/form/FormControlLabel';
import { useForm } from '../../../../../../shared/frontend/components/form/useForm';
import { Col } from '../../../../../../shared/frontend/components/grid/Col';
import { Row } from '../../../../../../shared/frontend/components/grid/Row';
import Modal from '../../../../../../shared/frontend/components/modal/modal';
import { Button, IButtonProps } from '../../../../../../shared/frontend/components/presentational/buttons/Button';
import { RioSelect } from '../../../../../../shared/frontend/components/presentational/select/Select';
import { Stack } from '../../../../../../shared/frontend/components/stack/Stack';
import { formatMsShortNoHours, relativeDateIfRecent } from '../../../../../../shared/frontend/libs/utils';
import { useAsync } from '../../../../../../shared/frontend/scripts/hooks/useAsync';
import { allRaidsSelector, claimedGuildsSelector } from '../../../../../../shared/frontend/scripts/selectors/global';
import { asset } from '../../../../../../shared/frontend/services/assetResolverService';
import { RAID_DIFFICULTY_LIST } from '../../../../shared/enums';
import { groupCompositionCompareItemValidationSchema } from '../../../../shared/raids';
import {
  IGetGroupCompositionCompareItem,
  TGroupCompositionCompareResultItem
} from '../../../../shared/types/raidTypes';
import { apiServiceAsync } from '../../services/apiService';
import {
  EntityAsyncSelect,
  TGuildSuggestionOption,
  TSuggestionOption
} from '../../../../../../shared/frontend/components/entityAsyncSelect';
import { TGuildGroupSummary } from '../../../../shared/types/authTypes';

interface IEditGroupCompositionModalProps {
  isOpen: boolean;
  onClose: () => void;
  value?: IGetGroupCompositionCompareItem;
  onSubmit: (filterItem: IGetGroupCompositionCompareItem) => void;
  onDelete?: () => void;
  title?: ReactNode;
  isCreate?: boolean;
  otherItems?: IGetGroupCompositionCompareItem[];
  saveButtonProps?: IButtonProps;
  showPullSelect?: boolean;
  data?: TGroupCompositionCompareResultItem;
}

export const GroupCompositionModal = ({
  isOpen,
  onClose,
  value: values,
  onSubmit,
  onDelete,
  title = t('composition-compare:modal.default-title'),
  isCreate = false,
  saveButtonProps,
  otherItems,
  showPullSelect = true,
  data
}: IEditGroupCompositionModalProps) => {
  const handleFilterChange = (newFilterItem: IGetGroupCompositionCompareItem) => {
    onSubmit(newFilterItem);
    onClose();
  };

  return (
    <Modal title={title} centered isOpen={isOpen} onRequestClose={onClose}>
      <Modal.Body>
        <EditItemForm
          value={values}
          onChange={handleFilterChange}
          onCancel={onClose}
          onDelete={onDelete}
          isCreate={isCreate}
          otherItems={otherItems}
          saveButtonProps={saveButtonProps}
          showPullSelect={showPullSelect}
          data={data}
        />
      </Modal.Body>
    </Modal>
  );
};

interface IEditItemFormProps {
  value?: IGetGroupCompositionCompareItem;
  onChange: (filterItem: IGetGroupCompositionCompareItem) => void;
  onCancel: () => void;
  onDelete?: () => void;
  isCreate?: boolean;
  otherItems?: IGetGroupCompositionCompareItem[];
  saveButtonProps?: IButtonProps;
  showPullSelect?: boolean;
  data?: TGroupCompositionCompareResultItem;
}

const EditItemForm = ({
  value,
  onChange,
  onCancel,
  onDelete,
  isCreate,
  otherItems,
  saveButtonProps,
  showPullSelect = true,
  data
}: IEditItemFormProps) => {
  const form = useForm<IGetGroupCompositionCompareItem>({
    initialValues: value,
    // Cast needed because our simple validator matches Joi's runtime interface but not its full type
    validationSchema: groupCompositionCompareItemValidationSchema as any
  });

  const userGuilds = useSelector(claimedGuildsSelector) as TGuildGroupSummary[];

  const userGuildsOptions = useMemo(() => {
    return userGuilds.map(({ guild }) => guild);
  }, [userGuilds]);

  const pullParams = useMemo(() => {
    return {
      region: form.values.region,
      realm: form.values.realm,
      guild: form.values.name,
      raid: form.values.raid,
      difficulty: form.values.difficulty,
      boss: form.values.boss,
      limit: 99999
    };
  }, [form.values]);

  const pulls = useAsync(() => apiServiceAsync.getGuildBossAttempts(pullParams), [JSON.stringify(pullParams)], {
    enabled: Object.values(pullParams).every(Boolean)
  });

  const pullData = pulls.data ? pulls.data.bossAttempts : [];

  const sortedPullData = useMemo(() => {
    const sorted = _.orderBy(pullData, 'pull_started_at', 'desc');

    const firstKillIndex = sorted.reduce((acc, pull, index) => {
      if (pull.is_success) {
        return index;
      }

      return acc;
    }, -1);

    const firstKill = firstKillIndex !== -1 ? sorted[firstKillIndex] : undefined;

    if (firstKill) {
      const temp = sorted[2];
      sorted[2] = { ...firstKill, isFirstKill: true };
      sorted[firstKillIndex] = temp;
    }

    return sorted.filter(Boolean);
  }, [pullData]);

  useEffect(() => {
    if (pulls.fetched) {
      const currentIndex = sortedPullData.findIndex((p) => p.id === form.values.pull);

      if (currentIndex === -1) {
        const pullId = undefined;
        form.onChangeValues({ pull: pullId }, { submitOnChange });
      }
    }
  }, [pulls.fetched]);

  const raids = useSelector(allRaidsSelector);

  useEffect(() => {
    const updatedValues: Partial<IGetGroupCompositionCompareItem> = { ...form.values };

    const lastItem = _.last(otherItems);

    if (!form.values.raid) {
      updatedValues.raid = lastItem?.raid ?? raids[0].slug;
    }

    if (!form.values.difficulty) {
      updatedValues.difficulty = lastItem?.difficulty ?? 'mythic';
    }

    if (!form.values.boss) {
      const latestValueBoss = lastItem?.boss ?? raids.find((r) => r.slug === lastItem?.raid)?.encounters[0].slug;

      if (latestValueBoss) {
        updatedValues.boss = latestValueBoss;
      } else {
        updatedValues.boss = 'latest';
      }
    }

    if (!_.isEqual(updatedValues, form.values)) {
      form.onChangeValues(updatedValues);
    }
  }, [form.values, otherItems]);

  const currentRaid = raids.find((r) => r.slug === form.values.raid);

  const getPullHelpText = () => {
    if (form.errors.pull) {
      return <FormControlHelpText>{form.errors.pull.message}</FormControlHelpText>;
    }

    if (sortedPullData.length === 0 && pulls.fetched) {
      return (
        <FormControlHelpText>
          <i className="fa-solid fa-circle-info" /> No pulls found
        </FormControlHelpText>
      );
    }
  };

  const userGuildsOptionsToAsyncSelect = useMemo<TGuildSuggestionOption[]>(() => {
    return userGuilds.map(({ guild }) => ({
      type: 'guild',
      data: guild,
      name: guild.name
    }));
  }, [userGuilds]);

  const submitOnChange = false;

  const splitter = '___';

  const getGuildOptionValue = ({ region, realm, name }: { region: string; realm: string; name: string }) => {
    return [region, realm, name].join(splitter);
  };

  const parseGuildOptionValue = (value: string) => {
    const [region, realm, name] = value.split(splitter);
    return { region, realm, name };
  };

  return (
    <Form labelSize="medium" form={form} onSubmit={form.formSubmitHandler(onChange)}>
      <FormControl required hasError={!!form.errors.name}>
        <FormControlLabel>Guild</FormControlLabel>
        <Stack spacingInRems={0}>
          <EntityAsyncSelect
            onChange={(value) => {
              const { name, realm, region } = parseGuildOptionValue(value);
              form.onChangeValues(
                {
                  name,
                  realm,
                  region
                },
                { submitOnChange }
              );
            }}
            type="guild"
            placeholder="Search for guild"
            value={
              form.values.region && form.values.realm && form.values.name
                ? getGuildOptionValue({ region: form.values.region, realm: form.values.realm, name: form.values.name })
                : undefined
            }
            getOptionValue={(option) =>
              option.type === 'guild'
                ? getGuildOptionValue({
                    region: option.data.region.slug,
                    realm: option.data.realm.slug,
                    name: option.data.name
                  })
                : ''
            }
            minSearchLengthToLoadOptions={2}
            theme="rio"
            autoFocus={isCreate}
            getDefaultOnChange={{
              searchTerm: form.values.name,
              filter: (option: TSuggestionOption) => {
                if (option.type !== 'guild' || option.data.name !== form.values.name) {
                  return false;
                }

                if (form.values.region && option.data.region.slug !== form.values.region) {
                  return false;
                }

                if (form.values.realm && option.data.realm.slug !== form.values.realm) {
                  return false;
                }

                return true;
              }
            }}
            styles={{
              container: (base) => ({
                ...base,
                flex: '1 1 auto'
              })
            }}
            options={userGuildsOptionsToAsyncSelect}
          />

          {userGuildsOptions.length > 0 && (
            <RioSelect
              theme="rio"
              size="small"
              options={userGuildsOptionsToAsyncSelect}
              placeholder="My guilds"
              formatOptionLabel={(guild) => (
                <span>
                  <span className={`rio-faction-color--${guild.data.faction}`}>{`<${guild.name}> `}</span>{' '}
                  {`${guild.data.realm.name}-${t(`regions:${guild.data.region.slug}.short-name`)}`}
                </span>
              )}
              getOptionValue={(guild) => guild.name}
              isClearable={false}
              isSearchable={false}
              styles={{
                container: (base) => ({
                  ...base,
                  minWidth: 120
                }),
                menu: (base) => ({
                  ...base,
                  right: 0,
                  minWidth: 320
                })
              }}
              onChange={(_, guild) => {
                form.onChangeValues(
                  { name: guild.name, realm: guild.data.realm.slug, region: guild.data.region.slug },
                  { submitOnChange }
                );
              }}
            />
          )}
        </Stack>
        {form.errors.name && <FormControlHelpText>{form.errors.name.message}</FormControlHelpText>}
      </FormControl>

      <FormControl required hasError={!!form.errors.raid}>
        <FormControlLabel>Raid</FormControlLabel>
        <RioSelect
          theme="rio"
          options={raids}
          value={form.values.raid}
          isClearable={false}
          onChange={(slug) => {
            form.onChangeValues({ raid: slug!, boss: 'latest' }, { submitOnChange });
          }}
          getOptionValue={(raid) => raid.slug}
          formatOptionLabel={(raid) => {
            return (
              <Stack alignItems="center">
                <Avatar size={16} src={asset(raid.icon_url)} />
                <span>{t(`raids:${raid.slug}.name`)}</span>
              </Stack>
            );
          }}
        />
        {form.errors.raid && <FormControlHelpText>{form.errors.raid.message}</FormControlHelpText>}
      </FormControl>

      <FormControl required>
        <Row gutter="x-small">
          <Col size={6}>
            <FormControl required hasError={!!form.errors.boss}>
              <FormControlLabel>Boss</FormControlLabel>
              <RioSelect
                theme="rio"
                options={['latest', ...(currentRaid?.encounters.map((e) => e.slug) ?? [])]}
                value={form.values.boss}
                disabled={!currentRaid}
                placeholder="Select Boss"
                onChange={(slug) => form.onChangeValues({ boss: slug! }, { submitOnChange })}
                formatOptionLabel={(slug) => {
                  if (slug === 'latest') {
                    return 'Latest';
                  }

                  const encounter = currentRaid?.encounters.find((e) => e.slug === slug);

                  if (!encounter) {
                    return null;
                  }

                  return (
                    <Stack alignItems="center">
                      <Avatar size={16} src={asset(encounter.iconUrl)} />
                      <span>{encounter.name}</span>
                    </Stack>
                  );
                }}
              />
              {form.errors.boss && <FormControlHelpText>{form.errors.boss.message}</FormControlHelpText>}
            </FormControl>
          </Col>
          <Col size={6}>
            <FormControl required hasError={!!form.errors.difficulty}>
              <FormControlLabel>Difficulty</FormControlLabel>
              <RioSelect
                theme="rio"
                options={[...RAID_DIFFICULTY_LIST].reverse()}
                getOptionValue={(difficulty) => difficulty.slug}
                getOptionLabel={(difficulty) => t(`common:difficulty.${difficulty.slug}.name`)}
                value={form.values.difficulty}
                onChange={(difficulty) => form.onChangeValues({ difficulty: difficulty! }, { submitOnChange })}
              />
              {form.errors.difficulty && <FormControlHelpText>{form.errors.difficulty.message}</FormControlHelpText>}
            </FormControl>
          </Col>

          {showPullSelect && (
            <Col size={12}>
              <FormControl hasError={!!form.errors.pull}>
                <FormControlLabel>Pull</FormControlLabel>
                <RioSelect
                  isLoading={pulls.loading}
                  theme="rio"
                  isClearable
                  placeholder="Select Pull"
                  options={sortedPullData}
                  getOptionValue={(pull: any) => pull.id}
                  disabled={sortedPullData.length === 0}
                  formatOptionLabel={(pull) => {
                    const index = sortedPullData.findIndex((p) => p.id === pull.id);

                    const totalRosterMembers = pull.composition
                      ? Object.values(pull.composition).reduce<number>((a, b: number) => a + b, 0)
                      : 0;

                    let suffix = '';
                    let className = '';
                    const totalRosterMembersString = totalRosterMembers ? `(${totalRosterMembers} members)` : '';

                    if (pull.is_reset) {
                      suffix = '(Reset)';
                      className = 'text-muted';
                    } else if (pull.is_success) {
                      suffix = `(${formatMsShortNoHours(pull.duration_ms)} - Success)`;
                      className = 'text-raiderio text-bold';
                    } else {
                      suffix =
                        pull.overall_percent === null
                          ? `(${formatMsShortNoHours(pull.duration_ms)} - Wiped)`
                          : `(${formatMsShortNoHours(pull.duration_ms)} - Wiped at ${pull.overall_percent.toFixed(2)}%)`;
                      className = 'text-white';
                    }

                    if (index === 0) {
                      return (
                        <span className={className}>
                          Latest pull <span className="text-italic">{suffix}</span> {totalRosterMembersString}
                        </span>
                      );
                    }

                    if (index === 1) {
                      return (
                        <span className={className}>
                          Pull before last <span className="text-italic">{suffix}</span> {totalRosterMembersString}
                        </span>
                      );
                    }

                    if (pull.isFirstKill) {
                      return (
                        <span className={className}>
                          First kill <span className="text-italic">{suffix}</span> {totalRosterMembersString}
                        </span>
                      );
                    }

                    return (
                      <span className={className}>
                        {relativeDateIfRecent(pull.pull_started_at, 24, true)}{' '}
                        <span className="text-italic">{suffix}</span> {totalRosterMembersString}
                      </span>
                    );
                  }}
                  value={form.values.pull}
                  onChange={(pull) => form.onChangeValues({ pull: pull || undefined }, { submitOnChange })}
                />

                {getPullHelpText()}
              </FormControl>
            </Col>
          )}
        </Row>
      </FormControl>

      <FormControl>
        <Stack justifyContent="space-between">
          {onDelete ? (
            <Button variant="destructive" onClick={onDelete} icon={<i className="fa-solid fa-trash" />}>
              {t('settings:shared.buttons.delete')}
            </Button>
          ) : (
            <div />
          )}

          <Stack justifyContent="flex-end">
            <Button onClick={onCancel}>Cancel</Button>
            <Button
              icon={<i className="fa-solid fa-floppy-disk" />}
              variant="primary"
              type="submit"
              disabled={!form.isDirty}
              {...saveButtonProps}
            >
              {saveButtonProps?.children ?? t('settings:shared.buttons.save')}
            </Button>
          </Stack>
        </Stack>
      </FormControl>
    </Form>
  );
};
